import { extractExifMetadata } from '../services/exifService';
import { resolvePhotoTimezone } from '../services/timezoneResolver';
import {
  ProcessPhotoJob,
  ProcessPhotoResult,
  WorkerInMessage,
  WorkerOutMessage,
} from './workerPool';

/**
 * Calculates target dimensions for HD preview scaling (max 1600px, preserving aspect ratio).
 */
export function calculatePreviewDimensions(
  origWidth: number,
  origHeight: number,
  maxDimension = 1600
): { width: number; height: number } {
  if (origWidth <= 0 || origHeight <= 0) {
    return { width: maxDimension, height: maxDimension };
  }
  const maxSide = Math.max(origWidth, origHeight);
  if (maxSide <= maxDimension) {
    return { width: origWidth, height: origHeight };
  }
  const scale = maxDimension / maxSide;
  return {
    width: Math.max(1, Math.round(origWidth * scale)),
    height: Math.max(1, Math.round(origHeight * scale)),
  };
}

/**
 * Calculates 1:1 square center-crop coordinates for map thumbnail generation.
 */
export function calculateCenterCropCoordinates(
  width: number,
  height: number
): { sx: number; sy: number; sw: number; sh: number } {
  const side = Math.min(width, height);
  const sx = Math.max(0, Math.round((width - side) / 2));
  const sy = Math.max(0, Math.round((height - side) / 2));
  return { sx, sy, sw: side, sh: side };
}

/**
 * Executes the complete single-decode dual-WebP image processing pipeline.
 */
export async function processPhoto(job: ProcessPhotoJob): Promise<ProcessPhotoResult> {
  const {
    id,
    file,
    category,
    tripTimezone,
    calibratedDriftSeconds,
    manualOffsetOverrideSeconds,
  } = job;

  // 1. Chunked EXIF metadata extraction
  const exifData = await extractExifMetadata(file);

  // 2. 4-tier Timezone reconciliation to UTC timestamp
  const tzResult = resolvePhotoTimezone(
    exifData,
    { timezone_id: tripTimezone } as any,
    undefined,
    calibratedDriftSeconds,
    manualOffsetOverrideSeconds
  );

  const timestampUtc = tzResult.timestampUtc;
  const dateTimeOriginal = exifData.dateTimeOriginal || '';
  const cameraExif = exifData.camera;

  let initialBitmap: ImageBitmap | null = null;
  let previewBitmap: ImageBitmap | null = null;
  let previewCanvas: OffscreenCanvas | null = null;
  let thumbCanvas: OffscreenCanvas | null = null;

  try {
    if (typeof createImageBitmap !== 'function') {
      throw new Error('createImageBitmap is not available in current environment');
    }
    if (typeof OffscreenCanvas === 'undefined') {
      throw new Error('OffscreenCanvas is not available in current environment');
    }

    // 3. Decode image with native orientation normalization
    try {
      initialBitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      initialBitmap = await createImageBitmap(file);
    }
    const { width: targetW, height: targetH } = calculatePreviewDimensions(
      initialBitmap.width,
      initialBitmap.height,
      1600
    );

    // Apply decode-time scaling if necessary
    if (initialBitmap.width !== targetW || initialBitmap.height !== targetH) {
      previewBitmap = await createImageBitmap(initialBitmap, {
        resizeWidth: targetW,
        resizeHeight: targetH,
        resizeQuality: 'high',
      });
      initialBitmap.close();
      initialBitmap = null;
    } else {
      previewBitmap = initialBitmap;
      initialBitmap = null;
    }

    const previewW = previewBitmap.width;
    const previewH = previewBitmap.height;

    // 4. Generate HD Preview WebP (1600px max @ 0.78 quality)
    previewCanvas = new OffscreenCanvas(previewW, previewH);
    const pCtx = previewCanvas.getContext('2d', { alpha: false });
    if (!pCtx) {
      throw new Error('Failed to acquire 2d context for preview canvas');
    }
    pCtx.imageSmoothingEnabled = true;
    pCtx.imageSmoothingQuality = 'high';
    pCtx.drawImage(previewBitmap, 0, 0, previewW, previewH);

    const previewBlob = await previewCanvas.convertToBlob({
      type: 'image/webp',
      quality: 0.78,
    });

    // 5. Generate Map Thumbnail WebP (400x400 center-cropped @ 0.80 quality)
    thumbCanvas = new OffscreenCanvas(400, 400);
    const tCtx = thumbCanvas.getContext('2d', { alpha: false });
    if (!tCtx) {
      throw new Error('Failed to acquire 2d context for thumbnail canvas');
    }
    tCtx.imageSmoothingEnabled = true;
    tCtx.imageSmoothingQuality = 'high';

    const { sx, sy, sw, sh } = calculateCenterCropCoordinates(previewW, previewH);
    tCtx.drawImage(previewBitmap, sx, sy, sw, sh, 0, 0, 400, 400);

    const thumbnailBlob = await thumbCanvas.convertToBlob({
      type: 'image/webp',
      quality: 0.80,
    });

    return {
      id,
      category,
      timestampUtc,
      thumbnailBlob,
      previewBlob,
      width: previewW,
      height: previewH,
      exif: cameraExif,
      dateTimeOriginal,
    };
  } finally {
    // 6. Explicit GPU & Memory deallocation
    if (initialBitmap) {
      try {
        initialBitmap.close();
      } catch {}
    }
    if (previewBitmap) {
      try {
        previewBitmap.close();
      } catch {}
    }
    if (previewCanvas) {
      previewCanvas.width = 0;
      previewCanvas.height = 0;
    }
    if (thumbCanvas) {
      thumbCanvas.width = 0;
      thumbCanvas.height = 0;
    }
  }
}

// Web Worker message listener (only registers when running inside a real Web Worker, not main window)
const isDedicatedWorkerScope =
  typeof window === 'undefined' &&
  typeof self !== 'undefined' &&
  typeof (self as any).postMessage === 'function' &&
  typeof (self as any).addEventListener === 'function';

if (isDedicatedWorkerScope) {
  self.addEventListener('message', async (e: MessageEvent<WorkerInMessage>) => {
    const msg = e.data;
    if (msg.type === 'PROCESS_PHOTO') {
      try {
        const result = await processPhoto(msg.payload);
        (self as any).postMessage({ type: 'SUCCESS', payload: result } as WorkerOutMessage);
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : String(err);
        (self as any).postMessage({
          type: 'ERROR',
          id: msg.payload.id,
          error: errMsg,
        } as WorkerOutMessage);
      }
    } else if (msg.type === 'TERMINATE') {
      self.close();
    }
  });
}
